import { Alert, Backdrop, CircularProgress, Snackbar } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { userHasOnlyStudentRole } from '../auth';
import { apiPaths, authHeader, handleResponse } from '../queryClient';
import { useSignalREffect } from '../signalr/SignalRContext';
import SimulatorControls from '../simulator-controls/SimulatorControls';
import StopSimulationDialog from '../stop-simulation-dialog/StopSimulationDialog';
import {
  ActiveSimulation as IActiveSimulation,
  SimulationEventMessage,
  SimulationRole,
  SimulationSignal,
  SimulationState,
} from '../types';
import { isRunning } from '../utils/activeSimulation';
import { useActiveClassroom, useActiveSimulation } from './useActiveSimulation';

export interface ActiveSimulationControlsType {
  [key: string]: {
    dialog: boolean;
    title: string;
    description: string;
    action: any;
  };
}

const ActiveSimulation = () => {
  const [showDialog, setShowDialog] = useState(false);
  const isStudent = userHasOnlyStudentRole();
  const { activeSimulation, setActiveSimulation } = useActiveSimulation();
  const { setActiveClassroom } = useActiveClassroom();
  const [waiting, setWaiting] = useState(false);

  // We need this check(recall active api) because the response from control API
  // provide statuses Accepted(200) or Not found(404),
  // this doesn't mean that Simulation was completed(sto[ped).
  const checkForActiveSimulation = async () =>
    await fetch(`/api/${apiPaths.activeSimulations}`, {
      headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
      method: 'GET',
    }).then(({ status }) => (status === 200 || status === 404 ? checkForActiveSimulation() : setWaiting(false)));

  const { mutate: controlSimulation } = useMutation<
    Response,
    Response,
    any,
    { previousActiveSimulation: IActiveSimulation }
  >({
    // @ts-ignore
    async mutationFn(signal) {
      setWaiting(true);

      return fetch(`/api/session/${activeSimulation?.id}/control`, {
        body: JSON.stringify({ signal }),
        headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
        method: 'POST',
      })
        .then(handleResponse)
        .then(() => (signal === SimulationSignal.Stop ? checkForActiveSimulation() : setWaiting(false)));
    },
    onSuccess: () => {},
    onMutate: () => {
      return { previousActiveSimulation: activeSimulation };
    },
    onError: (errorResponse, signal, context) => {
      setWaiting(false);
      setActiveSimulation(context?.previousActiveSimulation ?? null);
    },
  });

  const { mutate: abandonSimulation } = useMutation<
    Response,
    Response,
    string | undefined,
    { previousActiveSimulation: IActiveSimulation }
  >({
    // @ts-ignore
    async mutationFn(sessionId) {
      setShowLoadingBackdrop(true);

      return fetch(apiPaths.abandon(sessionId), {
        headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason: 'Simulation aborted successfully.',
        }),
        method: 'POST',
      }).then(handleResponse);
    },
    onSuccess: () => setWaiting(false),
    onMutate: () => {
      setShowLoadingBackdrop(false);

      return { previousActiveSimulation: activeSimulation };
    },
    onError: (errorResponse, signal, context) => {
      setWaiting(false);
      setActiveSimulation(context?.previousActiveSimulation ?? null);
    },
  });

  const [signalState, setSignalState] = useState<SimulationSignal>();
  const [showLoadingBackdrop, setShowLoadingBackdrop] = useState(false);
  const [showPopupToast, setShowPopupToast] = useState(false);

  useSignalREffect(
    SimulationEventMessage.SimulationCompleted,
    () => {
      setActiveClassroom(null);
      setActiveSimulation(null);
      setShowLoadingBackdrop(false);
    },
    [],
  );

  const activeControl: ActiveSimulationControlsType = {
    [SimulationSignal.Pause]: {
      action: controlSimulation,
      description: 'Are you sure you want to pause this simulation?',
      dialog: false,
      title: 'Pause simulation',
    },
    [SimulationSignal.Play]: {
      action: signal => controlSimulation(signal),
      description: 'Are you sure you want to play this simulation?',
      dialog: false,
      title: 'Play simulation',
    },
    [SimulationSignal.Stop]: {
      action: signal => controlSimulation(signal),
      dialog: true,
      description: 'Are you sure you want to stop this simulation?',
      title: 'Stop simulation',
    },
    [SimulationSignal.Abandon]: {
      action: () => abandonSimulation(activeSimulation?.id),
      dialog: true,
      description: 'This will forcefully stop the simulation. Do you want to continue?',
      title: 'Abort simulation',
    },
  };

  const handleClickControl = (signal: SimulationSignal) => {
    setShowPopupToast(false);

    if (activeSimulation?.state === SimulationState.Unknown) {
      setShowPopupToast(true);
    } else {
      setShowDialog(activeControl[signal].dialog);
    }

    setSignalState(signal);

    !activeControl[signal].dialog && activeControl[signal]?.action(signal);
  };

  return (
    <>
      {activeSimulation && isRunning(activeSimulation) && activeSimulation.role === SimulationRole.Instructor && (
        <SimulatorControls
          waiting={waiting}
          {...activeSimulation}
          isStudent={isStudent}
          onClickControl={handleClickControl}
        />
      )}

      {signalState && (
        <StopSimulationDialog
          description={activeControl[signalState].description}
          onClickCancel={() => setShowDialog(false)}
          onClickStop={() => {
            setShowDialog(false);
            activeControl[signalState].action(signalState);
          }}
          open={showDialog}
          title={activeControl[signalState].title}
        />
      )}

      <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={showPopupToast}>
        <Alert onClose={() => setShowPopupToast(false)} severity="error">
          Preparing your simulator. Please wait a few seconds before you try again.
        </Alert>
      </Snackbar>

      <Backdrop
        sx={theme => ({ color: theme.palette.primary.contrastText, zIndex: theme.zIndex.drawer + 1 })}
        open={showLoadingBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default ActiveSimulation;
